import {
  Avatar as ChakraAvatar,
  AvatarProps as ChakraAvatarProps,
  useImage,
  useMultiStyleConfig
} from '@chakra-ui/react';
import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';

import { forwardRef } from '../../system';
import Icon from '../Icons/Icon';

import DisplayNames from './enums';
import getInitials from './getInitials';
import getVariant from './getVariant';

export type AvatarSizes = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
export type AvatarVariantOptions = 'mint' | 'orange' | 'teal' | 'indigo' | 'purple' | 'pink';
type LoadingOptions = 'eager' | 'lazy';

export const variantPropTypes: AvatarVariantOptions[] = ['mint', 'orange', 'teal', 'indigo', 'purple', 'pink'];
export const sizePropTypes: AvatarSizes[] = ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'];
export const loadingOptions: LoadingOptions[] = ['eager', 'lazy'];

export const propTypes = {
  name: PropTypes.string,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  size: PropTypes.oneOf(sizePropTypes),
  ignoreFallback: PropTypes.bool,
  onError: PropTypes.func,
  loading: PropTypes.oneOf(loadingOptions),
  variant: PropTypes.oneOf([...variantPropTypes, ''])
};

export const defaultProps = {
  name: '',
  size: 'md',
  ignoreFallback: false,
  loading: 'eager' as LoadingOptions,
  variant: ''
};

export type AvatarProps = Omit<ChakraAvatarProps, 'variant'> & {
  variant?: string;
};
const Avatar = forwardRef<AvatarProps, 'span'>((props: AvatarProps & typeof defaultProps, ref) => {
  const { variant: variantProp, name, size, src, onError, ignoreFallback, __css, ...rest } = props;

  const loadingStatus = useImage({ src, onError, ignoreFallback });

  const variant = getVariant(props, loadingStatus);

  const { container: avatarStyles, label: labelStyles } = useMultiStyleConfig('Avatar', {
    ...props,
    variant
  });

  const isSillhouette = !name && !src;

  const baseProps = {
    __css: mergeWith(avatarStyles, __css),
    getInitials: () => getInitials(name, size),
    icon: <Icon __css={labelStyles} boxSize="80%" marginTop="25%" type="UserAvatar" />,
    name,
    size,
    variant,
    src,
    onError,
    ignoreFallback,
    ref
  };

  return <ChakraAvatar data-sillhouette={isSillhouette} {...baseProps} {...rest} />;
});

Avatar.displayName = DisplayNames.Avatar;
Avatar.propTypes = propTypes;
Avatar.defaultProps = defaultProps;

export default Avatar;
